import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Stack,
  Link,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import logo from "src/assets/svg-icons/logo.svg";
import CompanyDetails from "./steps/company-details";
import CalculateSales from "./steps/calculate-sales";
import CalculateCOGS from "./steps/calculate-cogs";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AddAggregators from "./steps/add-aggregators";
import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import AggregatorDetails from "./steps/aggregator-details";
import MakeBalance from "./steps/make-balance";
import Summary from "./steps/summary";
import { COGSTypes, FormModeTypes, IBranchData } from "src/types/generic";
import { defaultLottieOptions, initSteps, schema, FormData } from "./constants";
import { BorderLinearProgress } from "./border-linear-progress";
import {
  useCreateJourneyMutation,
  useProcessFoodicsSalesMutation,
  useDeleteJourneyPaymentMethods,
  useGetAllJourneysDateRange,
  useGetCompany,
  useGetDraftJourney,
  useGetJourneyById,
  useGetLastCogs,
  useGetLastJourneyData,
  useUpdateCompanyMutation,
  useUpdateJourneyMutation,
  useProcessManualProductSalesMutation,
  useProcessExcelSalesMutation,
} from "src/api/add-sales";
import { LoadingButton } from "@mui/lab";
import {
  useNavigate,
  useParams,
  useSearchParams,
  Link as RouterLink,
} from "react-router-dom";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { foodicsTypes, IPayment } from "src/types/add-sales";
import { useGetBranches, useGetPaymentMethods } from "src/api/generic";
import Lottie from "react-lottie";
import { useGetAggregatorsListWithLastSale } from "src/api/aggreagators";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { useTranslation } from "react-i18next";
import BranchPackagePopup from "./branch-package-popup";
import { useLogoutMutation } from "src/api/auth";
import { authState } from "src/store/auth";
import LanguageIcon from "@mui/icons-material/Language";
import englandIcon from "src/assets/svg-icons/flags/england.svg";
import saudiIcon from "src/assets/svg-icons/flags/saudi.svg";
import { setLocalStoredData } from "src/utils/_localStorage";
import { ConstLocalStorage } from "src/constants/_localStorage";
import { specificBranchHasPermission } from "src/utils";
import { SalesPermissions } from "src/constants/permissions";

let defaultSteps = initSteps;

const AddAEditSalesPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const mode: FormModeTypes = !id ? "add" : "edit";
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  let [searchParams, setSearchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState(defaultSteps);
  const [foodicsType, setFoodicsType] =
    useState<foodicsTypes>("connectFoodics");
  const [COGSType, setCOGSType] = useState<COGSTypes>("manual");
  const [isOnboarding, setIsOnboarding] = useState<boolean>(false);
  const [isCompanyAdded, setIsCompanyAdded] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [balanceAmountMatch, setBalanceAmountMatch] = useState(true);
  const [balanceQntyMatch, setBalanceQntyMatch] = useState(true);
  const [selectedBranch, setSelectedBranch] = useState<
    IBranchData | undefined
  >();
  const [selectedBranchVat, setSelectedBranchVat] = useState(0);
  const [addedPackageToBranch, setAddedPackageToBranch] = useState(false);
  const [filesChanged, setFilesChanged] = useState(true);
  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation("addSales");
  const { t: generalT } = useTranslation("general");
  const [auth, setAuth] = useRecoilState(authState);
  const [anchorE2, setAnchorE2] = useState<null | HTMLElement>(null);
  const openE2 = Boolean(anchorE2);
  const autoImportBranchId = searchParams.get("importBranchId");
  const isAutoImportedJourney = !!autoImportBranchId; // auto imported by email

  // React hook form
  const {
    formState: { errors },
    trigger,
    getValues,
    setValue,
    reset,
    watch,
    control,
  } = useForm<FormData>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      mode: "add",
      journey_type: "manual",
      COGS_manual_type: "percentage",
      has_jazr_calculations: "yes",
      are_payments_new_agg: "yes",
      net_sales_with_tax: "",
      total_order_quantity: "",
      hasOtherRevenue: "no",
    },
  });
  const {
    fields,
    append,
    remove,
    replace: replaceAggregators,
  } = useFieldArray({
    name: "aggregators",
    control,
  });
  const {
    fields: balanceFields,
    append: balanceAppend,
    replace: replaceBalance,
    remove: balanceRemove,
  } = useFieldArray({
    name: "balance",
    control,
  });
  const {
    fields: newPaymentsAggFields,
    append: appendNewPaymentsAgg,
    replace: replaceNewPaymentsAgg,
    remove: removeNewPaymentsAgg,
  } = useFieldArray({
    name: "new_payments_aggregators",
    control,
  });
  const {
    fields: otherRevenueFields,
    append: appendOtherRevenue,
    remove: removeOtherRevenue,
  } = useFieldArray({
    name: "otherRevenuePayments",
    control,
  });

  const {
    fields: discountPaymentsFields,
    append: appendDiscountPayment,
    remove: removeDiscountPayment,
  } = useFieldArray({
    name: "discountPayments",
    control,
  });

  // APIS
  const {
    data: companyData,
    status: getCompanyStatus,
    isLoading,
    isFetching: isFetchingCompany,
  } = useGetCompany();
  const {
    mutate: mutateUpdateCompany,
    status: updateCompanyStatus,
    isPending: updateCompanyIsPending,
    data: updateCompanyResponse,
    error: updateCompanyError,
  } = useUpdateCompanyMutation(
    companyData?.data?.company?.id || searchParams.get("journeyId") || ""
  );
  const {
    mutate: mutateCreateOrders,
    isPending: isPendingCreateOrders,
    status: createOrdersStatus,
    error: createOrdersError,
  } = useProcessFoodicsSalesMutation();
  const {
    mutate: mutateProcessProductSales,
    isPending: isPendingProcessProductSales,
    status: processProductSalesStatus,
    error: processProductSalesError,
  } = useProcessManualProductSalesMutation();
  const {
    mutate: mutateProcessExcelSales,
    isPending: isPendingProcessExcelSales,
    status: processExcelSalesStatus,
    error: processExcelSalesError,
  } = useProcessExcelSalesMutation();

  const {
    data: journeyData,
    refetch: refetchJourney,
    isFetching,
    isLoading: isLoadingJourneyData,
    status: getJourneyStatus,
  } = useGetJourneyById({
    journeyId: id ?? searchParams.get("journeyId") ?? "",
    branch_id: mode === "add" ? (selectedBranch?.id as number) : undefined,
    enabled: !!id,
  });
  const {
    mutate: mutateCreateJourney,
    status: createJourneyStatus,
    isPending: isPendingCreateJourney,
    error: createJourneyError,
  } = useCreateJourneyMutation();
  const {
    mutate: mutateUpdateJourney,
    status: updateJourneyStatus,
    isPending: isPendingUpdateJourney,
    error: updateJourneyError,
  } = useUpdateJourneyMutation();
  const {
    data: branchesData,
    isLoading: isLoadingBranches,
    refetch: refetchBranches,
  } = useGetBranches();

  const { data: aggregatorsData } = useGetAggregatorsListWithLastSale({
    enabled: !!selectedBranch,
    branch_id: `${selectedBranch?.id}`,
  });

  const { data: paymentsData } = useGetPaymentMethods();

  const {
    data: draftJourneyData,
    status: getDraftJourneyStatus,
    isFetching: isFetchingDraftJourney,
  } = useGetDraftJourney({
    branch_id: `${selectedBranch?.id}`,
    enabled: !!selectedBranch && mode === "add" && !isAutoImportedJourney,
  });

  const { data: journeysDateRange } = useGetAllJourneysDateRange({
    branch_id: `${selectedBranch?.id}`,
    enabled: !!selectedBranch && mode === "add" && !isOnboarding,
  });

  const { data: lastCogsData } = useGetLastCogs({
    branch_id: `${selectedBranch?.id}`,
    enabled: !!selectedBranch && mode === "add" && !isOnboarding,
  });

  const {
    mutate: mutateLogout,
    status: logoutStatus,
    error: logoutError,
  } = useLogoutMutation();

  // const { data: lastJourneyData } = useGetLastJourneyData({
  //   branch_id: `${selectedBranch?.id}`,
  //   enabled: !!selectedBranch && mode === "add",
  // });

  const { mutate: mutateDeletePaymentMethods } =
    useDeleteJourneyPaymentMethods();

  // permissions
  const allBranches =
    branchesData?.data.filter((branch) => branch.status) || [];
  const branchesHavePermission =
    allBranches.filter((branch) =>
      specificBranchHasPermission(SalesPermissions.addSale, branch.id)
    ) || [];

  // Constants
  const journeyType = watch("journey_type");
  const aggregatorsStartStep = isOnboarding ? 4 : 3;
  const journeyTaxPercentage = selectedBranchVat / 100;
  const journeyTaxDivider = 1 + journeyTaxPercentage;
  const branchRequiresPackageRenew = // it mean renew after 1st subscription
    !selectedBranch?.active_subscriptions &&
    selectedBranch?.subscriptions &&
    selectedBranch.subscriptions.length > 0;

  const netSalesWithoutTax =
    watch("journey_type") === "foodics"
      ? watch("has_jazr_calculations") === "no"
        ? journeyData?.data?.sale?.foodics_net_sales
        : journeyData?.data?.sale?.gross_sales / journeyTaxDivider
      : watch("journey_type") === "excel"
      ? journeyData?.data?.sale?.gross_sales / journeyTaxDivider
      : +watch("net_sales_with_tax") / journeyTaxDivider;

  const hasBranchesList = allBranches?.length > 0;

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleLogout = () => {
    mutateLogout();
  };

  // Validations
  const validate = async () => {
    let stepFields = steps[currentStep].fields;
    if (currentStep === 0 && isCompanyAdded) {
      // Just choose branch if you have company added
      stepFields = ["branch_id"];
    }
    if (currentStep === 2 && COGSType === "recipe") {
      stepFields = [];
    }
    if (currentStep === 1 && watch("journey_type") === "manual") {
      stepFields = [
        ...(!getValues("branchHasMenuFeature") ? ["net_sales_with_tax"] : []),
        "total_order_quantity",
        "dateRange",
        "other_revenue",
        "other_revenue_tax",
        "otherRevenuePayments",
        "paymentDiscount",
        "discountPayments",
        "productSales",
      ];
    }
    if (currentStep === 1 && watch("journey_type") === "excel") {
      stepFields = [
        "total_order_quantity",
        "excelSales",
        "other_revenue",
        "other_revenue_tax",
        "otherRevenuePayments",
        "productSales",
      ];
    }
    if (
      currentStep === 1 &&
      watch("journey_type") === "foodics" &&
      journeyData?.data?.new_payment_methods?.length > 0 &&
      mode === "add" &&
      !isOnboarding
    ) {
      stepFields = [
        ...stepFields,
        "are_payments_new_agg",
        "new_payments_aggregators",
      ];
    }
    // Agg details step : validate step aggregator not all of them
    if (stepFields[0] === "aggregators") {
      const aggIndex = currentStep - aggregatorsStartStep;
      console.log({ aggIndex });
      stepFields = [`aggregators.${aggIndex}`];
    }
    const validStep = await trigger(stepFields as any[], { shouldFocus: true });
    return validStep;
  };

  // Go to next step
  const goNext = async () => {
    const valid = await validate();
    if (!valid) return;
    let revenuePaymentsTotal = 0;
    watch("otherRevenuePayments")?.forEach(
      (payment) => (revenuePaymentsTotal += Number(payment?.amount ?? 0))
    );
    let discountPaymentsTotal = 0;
    getValues("discountPayments")?.forEach(
      (payment) => (discountPaymentsTotal += Number(payment?.amount ?? 0))
    );
    const revenueShareWithTax =
      watch("include_other_revenue_tax") === "1"
        ? Number(watch("other_revenue"))
        : Number(watch("other_revenue")) + Number(watch("other_revenue_tax"));

    // Steps before Last step
    if (currentStep < steps.length - 1) {
      // Branch (1st) step
      if (currentStep === 0) {
        const companyDataToUpdate = {
          company: {
            name: getValues("company_name"),
          },
          branch: {
            name: getValues("branch_name"),
            city_id: +getValues("city_id"),
            has_vat: getValues("has_vat") === "yes",
            vat:
              getValues("has_vat") === "yes"
                ? getValues("vat")
                  ? Number(getValues("vat")) / 100
                  : 0
                : 0,
          },
        };
        if (isCompanyAdded) {
          if (
            !selectedBranch?.active_subscriptions &&
            !addedPackageToBranch &&
            mode === "add"
          ) {
            handleOpen(); //open to subscribe for 1st time
            return;
          }
          setCurrentStep((prev) => prev + 1);
        } else {
          mutateUpdateCompany(companyDataToUpdate);
        }
      } else if (
        currentStep === 1 &&
        watch("hasOtherRevenue") === "yes" &&
        revenuePaymentsTotal !== revenueShareWithTax
      ) {
        setNotifications([
          ...notifications,
          {
            type: "error",
            message: t("paymentsTotalShouldEqualOtherRevenue"),
          },
        ]);
        return;
      } else if (
        currentStep === 1 &&
        watch("hasPaymentDiscount") === "yes" &&
        discountPaymentsTotal !== Number(watch("paymentDiscount"))
      ) {
        setNotifications([
          ...notifications,
          {
            type: "error",
            message: `Payments amount total should equal total payment discount`,
          },
        ]);
        return;
      } else if (
        // Sales step
        (currentStep === 1 && journeyType === "foodics" && !filesChanged) ||
        (currentStep === 1 && journeyType === "foodics" && mode === "edit")
      ) {
        setCurrentStep((prev) => prev + 1);
      } else if (
        currentStep === 1 &&
        journeyType === "foodics" &&
        filesChanged
      ) {
        // Sales step : process foodics files
        mutateCreateOrders({
          journey_id: Number(searchParams.get("journeyId")) || 0,
        });
      } else if (
        watch("mode") === "add" &&
        currentStep === 1 &&
        journeyType === "manual" &&
        !!watch("productSales")
      ) {
        // Sales step : process manual product sales file
        mutateProcessProductSales({
          journey_id: Number(searchParams.get("journeyId")) || 0,
          start_date: watch("dateRange.start_date"),
          end_date: watch("dateRange.end_date"),
        });
      } else if (
        mode === "add" &&
        currentStep === 1 &&
        journeyType === "excel" &&
        filesChanged
      ) {
        // Sales step : process excel files
        mutateProcessExcelSales({
          journey_id: Number(searchParams.get("journeyId")) || 0,
        });
      } else if (
        // Sales step
        currentStep === 1 &&
        journeyType === "manual" &&
        mode === "add" &&
        !isOnboarding &&
        (moment(watch("dateRange").start_date).isAfter(
          moment(journeysDateRange?.end_date).add(1, "day")
        ) ||
          moment(watch("dateRange").end_date).isBefore(
            moment(journeysDateRange?.start_date).subtract(1, "day")
          ))
      ) {
        setNotifications([
          ...notifications,
          {
            type: "error",
            message: `${t("yourLastTimeRange")} ${
              journeysDateRange?.start_date
            } ${t("to")} ${journeysDateRange?.end_date}.  ${t(
              "pleaseEnsure"
            )} ${moment(journeysDateRange?.end_date)
              .add(1, "day")
              .format("YYYY-MM-DD")}.`,
          },
        ]);
        return;
      } else if (
        // Sales step : Lock date validation
        currentStep === 1 &&
        journeyType === "manual" &&
        mode === "add" &&
        !!selectedBranch?.close_date &&
        (moment(watch("dateRange").start_date).isSameOrBefore(
          moment(selectedBranch.close_date)
        ) ||
          moment(watch("dateRange").end_date).isSameOrBefore(
            moment(selectedBranch.close_date)
          ))
      ) {
        setNotifications([
          ...notifications,
          {
            type: "error",
            message: `${generalT("canNotAddEditOrDeleteBeforeLockDate")}: ${
              selectedBranch?.close_date
            }`,
          },
        ]);
        return;
      }

      // cogs step validation
      else if (
        currentStep === 2 &&
        mode === "add" &&
        COGSType === "manual" &&
        getValues("COGS_manual_type") === "amount" &&
        +getValues("COGS_value") > netSalesWithoutTax
      ) {
        setNotifications([
          ...notifications,
          {
            type: "error",
            message: `${t(
              "COGSMustNotExceedNetSales"
            )}: ${netSalesWithoutTax.toFixed(4)}`,
          },
        ]);
        return;
      }
      // end
      else if (currentStep === steps.length - 2) {
        // Balance step
        const accumulatedBalance = getValues("balance").reduce(
          (acc: { amount: number; quantity: number }, payment: any) => {
            acc.amount += +payment.amount;
            acc.quantity += +payment.quantity;
            return acc;
          },
          { amount: 0, quantity: 0 }
        );
        const netSalesWithTax =
          watch("journey_type") === "manual"
            ? +watch("net_sales_with_tax")
            : journeyData?.data.sale.gross_sales;

        const totalNetSalesWithTax = +netSalesWithTax;

        const totalQnty =
          watch("journey_type") === "manual"
            ? +watch("total_order_quantity")
            : journeyData?.data.payments.reduce(
                (sum: number, payment: any) => sum + payment.quantity,
                0
              );
        //validate balance total amount
        if (
          accumulatedBalance.amount.toFixed(2) !==
          totalNetSalesWithTax.toFixed(2)
        ) {
          setNotifications([
            ...notifications,
            {
              type: "error",
              message: t("totalPaymentsMethodsMust"),
            },
          ]);
          setBalanceAmountMatch(false);
          return;
        }
        //validate balance total qnty
        if (
          Number(accumulatedBalance.quantity.toFixed(2)) <
            Number(totalQnty.toFixed(2)) &&
          journeyType !== "manual"
        ) {
          setNotifications([
            ...notifications,
            {
              type: "error",
              message: t("totalPaymentsQntyMust"),
            },
          ]);
          setBalanceQntyMatch(false);
          return;
        }
        setCurrentStep((prev) => prev + 1);
      } else {
        // If none of above steps happen, just go next
        setCurrentStep((prev) => prev + 1);
      }
    }
    // Last step
    if (currentStep === steps.length - 1) {
      handleSubmitJourney();
    }
  };

  // Go to previous step
  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // Add and Delete aggregators
  const addAnotherAggregator = async () => {
    const valid = await validate();
    if (!valid) return;
    append({
      aggregator_name: "",
      aggregator_fee: "",
      payments: [],
      delivery_type: "order",
      delivery_cost: "0",
      net_sales_with_tax: "",
      order_quantity: "",
    });
    setCurrentStep((prev) => prev + 1);
  };
  const deleteAggregator = async () => {
    remove(currentStep - 4);
    setCurrentStep((prev) => prev - 1);
  };

  const handleClickE2 = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorE2(event.currentTarget);
  };

  const handleCloseE2 = () => {
    setAnchorE2(null);
  };

  const handleLanguageChange = (language: "en" | "ar") => {
    i18n.changeLanguage(language);
    setLocalStoredData(ConstLocalStorage.language, language);
    setAnchorE2(null);
  };

  // Submit journey
  const handleSubmitJourney = () => {
    const {
      other_revenue,
      hasOtherRevenue,
      other_revenue_tax,
      include_other_revenue_tax,
    } = getValues();
    const otherRevenueDetails =
      hasOtherRevenue === "yes"
        ? {
            include_other_revenue_tax: include_other_revenue_tax,
            other_revenue:
              include_other_revenue_tax === "1"
                ? +(Number(other_revenue) - Number(other_revenue_tax)).toFixed(
                    5
                  )
                : Number(other_revenue),
            other_revenue_tax: Number(other_revenue_tax),
          }
        : {
            other_revenue: 0, // reset it
            other_revenue_tax: 0, // reset it
          };
    const dataToSend = {
      ...(mode === "add" && {
        journey: {
          ...((journeyType === "foodics" || journeyType === "excel") && {
            id: Number(searchParams.get("journeyId")),
          }),
          type: journeyType,
          branch_id:
            Number(watch("branch_id")) ||
            journeyData?.data?.branch ||
            companyData?.data?.company?.branches[0]?.id,
          ...(journeyType === "manual" && {
            start_date: watch("dateRange.start_date"),
            end_date: watch("dateRange.end_date"),
          }),
          ...(!!journeyData?.data?.reference && {
            reference: journeyData?.data?.reference,
          }),
        },
      }),
      ...(mode === "add" &&
        watch("journey_type") === "manual" &&
        !!watch("productSales") && {
          product_sales_journey_id: Number(searchParams.get("journeyId")),
        }),
      ...(journeyType === "manual" && {
        sale: {
          quantity: Number(watch("total_order_quantity")),
          gross_sales: Number(watch("net_sales_with_tax")),
          discount: Number(watch("discount")) || 0,
          tips: Number(watch("tips")) || 0,
          journey_discount: Number(watch("paymentDiscount")) || 0,
          ...otherRevenueDetails,
        },
      }),
      ...((journeyType === "foodics" || journeyType === "excel") && {
        sale: {
          id: journeyData?.data?.sale?.id,
          discount: Number(watch("discount")) || 0,
          tips: Number(watch("tips")) || 0,
          ...(mode === "add" && {
            tax_type:
              watch("has_jazr_calculations") === "yes" ? "jazr" : "foodics",
          }),
          ...(journeyType === "excel" && {
            quantity: Number(watch("total_order_quantity")),
          }),
          ...otherRevenueDetails,
        },
      }),
      cogs: {
        type: COGSType, // manual | foodics
        ...(COGSType === "manual" && {
          is_percentage: getValues("COGS_manual_type") === "percentage",
          amount:
            getValues("COGS_manual_type") === "percentage"
              ? +((getValues("COGS_value") / 100) * netSalesWithoutTax).toFixed(
                  7
                )
              : getValues("COGS_value"),
        }),
      },
      bankFee: {
        bank_fee: 0,
      },
      aggregators: [
        ...(getValues("aggregators")?.length > 0
          ? getValues("aggregators").map((aggregator) => {
              return {
                ...(!aggregator.aggregator_id && {
                  name: aggregator.aggregator_name, // name in case of new agg
                }),
                ...(!!aggregator.aggregator_id && {
                  id: aggregator.aggregator_id, // id in case of existing agg
                }),
                ...(aggregator?.payments && //old: journeyType === "foodics" && isOnboarding
                  aggregator?.payments?.length > 0 && {
                    payment_methods: aggregator?.payments?.map((str) =>
                      Number(str)
                    ),
                  }),
                aggregator_sale: {
                  aggregator_fee_percentage: +aggregator.aggregator_fee,
                  ...(!!aggregator.aggregator_sale_id && {
                    id: aggregator.aggregator_sale_id, // id in case of existing agg
                  }),
                  delivery_type:
                    aggregator.delivery_type === "order"
                      ? "by_order"
                      : "by_amount", // types: by_order | by_amount
                  delivery_fee: Number(aggregator.delivery_cost ?? 0),
                  ...(journeyType === "manual" && {
                    sales: Number(aggregator.net_sales_with_tax),
                    quantity: Number(aggregator.order_quantity),
                  }),
                },
              };
            })
          : []),
        // ...(getValues("new_payments_aggregators")?.length > 0
        //   ? getValues("new_payments_aggregators")?.map((aggregator) => {
        //       return {
        //         name: aggregator.aggregator_name,
        //         aggregator_fee: Number(aggregator.aggregator_fee),
        //         payment_methods: aggregator?.payments?.map((str) =>
        //           Number(str)
        //         ),
        //       };
        //     })
        //   : []),
      ],
      payments: [
        ...getValues("balance").map((payment, index) => {
          return {
            ...((journeyType === "foodics" ||
              journeyType === "excel" ||
              (journeyType === "manual" && !isOnboarding)) && {
              id: Number(payment.id),
            }),
            ...(journeyType === "manual" &&
              isOnboarding &&
              mode === "add" && {
                is_branch: payment.is_branch === true,
              }),
            quantity: Number(payment.quantity),
            total: Number(payment.amount),
            ...(!!payment.payment_method_id && {
              payment_method_id: payment.payment_method_id,
            }),
            name: payment.name,
            ...(payment.is_new_payment && {
              balance: Number(payment.start_balance),
            }),
          };
        }),
        ...(mode === "add" &&
        journeyType === "manual" &&
        getValues("hasPaymentDiscount") === "yes"
          ? getValues("discountPayments").map((payment, index) => {
              return {
                quantity: 0,
                total: Number(payment.amount),
                payment_method_id: payment.payment_method_id,
                name: payment.name,
              };
            })
          : []),
      ],

      otherRevenuePayments:
        hasOtherRevenue === "yes"
          ? getValues("otherRevenuePayments").map((payment) => {
              return {
                payment_method_id: Number(payment.payment_method_id),
                amount: Number(payment.amount),
              };
            })
          : [],
    };
    if (mode === "add") {
      mutateCreateJourney(dataToSend);
    } else {
      mutateUpdateJourney({ journey_id: id, ...dataToSend });
    }
  };

  useEffect(() => {
    if (!!lastCogsData?.data && mode === "add") {
      setValue("COGS_manual_type", "percentage");
      setValue("COGS_value", lastCogsData.data.percentage);
    }
  }, [lastCogsData]);

  useEffect(() => {
    const selectedBranch = allBranches.find(
      (branch) => branch.id === +watch("branch_id")
    );
    setSelectedBranchVat(Number(selectedBranch?.vat));
    setSelectedBranch(selectedBranch);
    setAddedPackageToBranch(false);
    // setValue(
    //   "branchHasMenuFeature",
    //   !!selectedBranch?.active_subscriptions?.package?.has_menu
    // );
    const currentValue = watch("branchHasMenuFeature");
    const newValue = !!selectedBranch?.active_subscriptions?.package?.has_menu;

    if (currentValue !== newValue) {
      setValue("branchHasMenuFeature", newValue);
    }
  }, [watch("branch_id"), allBranches]);

  // Add agg list steps if it's manual add sales
  useEffect(() => {
    console.log("re render");
    if (!isOnboarding && mode === "add") {
      if (journeyType === "manual") {
        // Add agg list steps if it's manual add sales
        replaceAggregators([]);
        aggregatorsData?.data.map((agg: any) => {
          console.log("happens");
          if (agg.status) {
            append({
              aggregator_name: agg.name,
              aggregator_id: agg.id,
              aggregator_fee: agg.sale?.aggregator_fee_percentage ?? undefined,
              payments: agg.related_payment_methods.map(
                (pay: any) => pay.payment_method?.id
              ),
              payments_Names: agg.related_payment_methods.map(
                (pay: any) => pay.payment_method?.name
              ),
              delivery_type:
                agg.sale?.delivery_type === "by_amount" || agg.is_keta
                  ? "amount"
                  : "order",
              delivery_cost:
                agg.sale?.delivery_type === "by_order"
                  ? agg.sale?.delivery_fee_per_order
                  : agg.sale?.delivery_fee,
              net_sales_with_tax: "",
              order_quantity: "",
              is_keta: agg.is_keta,
            });
          }
        });
      } else {
        replaceAggregators([]);
        // setSteps(defaultSteps);
      }
    }
  }, [journeyType, aggregatorsData]);

  // Aggregators fields change
  useEffect(() => {
    if (fields.length > 0) {
      const newSteps = fields.map((field, index) => {
        return {
          id: `aggregators_${index}`,
          name: `Aggregators ${index}`,
          fields: ["aggregators"],
        };
      });
      const newArr = [...defaultSteps];
      newArr.splice(aggregatorsStartStep, 0, ...newSteps);
      setSteps(newArr);
    } else {
      setSteps(defaultSteps);
    }
  }, [fields]);

  // Get company success and error handling
  useEffect(() => {
    if (getCompanyStatus === "success") {
      if (companyData?.data?.company?.is_onboarding) {
        setIsOnboarding(true);
        setValue("onboarding", "yes");
        defaultSteps = initSteps;
        setSteps(initSteps);
      } else {
        // remove has_aggregators step if it's add sales not onboarding
        const filteredSteps = defaultSteps.filter(
          (step) => step.id !== "has_aggregators"
        );
        defaultSteps = filteredSteps;
        setSteps(filteredSteps);
      }
      if (companyData?.data?.company?.branches?.length > 0) {
        setIsCompanyAdded(true);
      }
      // if (companyData?.data?.company?.branches?.[0]?.id) {
      //   setBranchId(companyData?.data?.company?.branches[0]?.id);
      // }
    }
  }, [getCompanyStatus, isFetchingCompany]);

  useEffect(() => {
    if (mode === "add" && !isAutoImportedJourney) {
      setSearchParams({});
    }
  }, []);

  // Get draft journey success and error handling
  useEffect(() => {
    if (getDraftJourneyStatus === "success" && mode === "add") {
      setSearchParams({
        journeyId: `${draftJourneyData.data.id}`,
      });
    }
  }, [getDraftJourneyStatus]);

  // Create order (sales step) success and error handling
  useEffect(() => {
    if (createOrdersStatus === "success") {
      setLoading(true);
      setFilesChanged(false);
      const delayedExecution = setTimeout(() => {
        refetchJourney();
        setLoading(false);
      }, 25000);

      return () => clearTimeout(delayedExecution);
    } else if (createOrdersStatus === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            createOrdersError?.data.errors?.[0].value ||
            generalT("somethingWrong"),
        },
      ]);
    }
  }, [createOrdersStatus]);

  // Process product sales (sales step) success and error handling
  useEffect(() => {
    if (processProductSalesStatus === "success") {
      setLoading(true);
      // setFilesChanged(false);
      const delayedExecution = setTimeout(() => {
        refetchJourney();
        setLoading(false);
      }, 8000);

      return () => clearTimeout(delayedExecution);
    } else if (processProductSalesStatus === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            processProductSalesError?.data.errors?.[0].value ||
            generalT("somethingWrong"),
        },
      ]);
    }
  }, [processProductSalesStatus]);

  // Process excel sales (sales step) success and error handling
  useEffect(() => {
    if (processExcelSalesStatus === "success") {
      setLoading(true);
      setFilesChanged(false);
      const delayedExecution = setTimeout(() => {
        refetchJourney();
        setLoading(false);
      }, 15000);

      return () => clearTimeout(delayedExecution);
    } else if (processExcelSalesStatus === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            processExcelSalesError?.data.errors?.[0].value ||
            generalT("somethingWrong"),
        },
      ]);
    }
  }, [processExcelSalesStatus]);

  // Balance fields Change
  useEffect(() => {
    if (
      (journeyType === "foodics" || journeyType === "excel") &&
      mode === "add"
    ) {
      replaceBalance([]);
      if (journeyData?.data.payments?.length > 0) {
        journeyData?.data.payments.forEach((payment: IPayment) => {
          balanceAppend({
            amount: `${payment.total}`,
            quantity: `${payment.quantity}`,
            id: `${payment.id}`,
            name: payment.paymentMethod.name,
            payment_method_id: `${payment.paymentMethod.id}`,
            start_balance: "0",
            is_new_payment: journeyData?.data.new_payment_methods?.some(
              (item: any) => item.id === payment.paymentMethod.id
            ),
            is_discount: payment.paymentMethod.is_discount,
          });
        });
      }

      // Add current aggregators linked to this foodics or excel files journey in add sales
      if (journeyData?.data.current_aggregators.length > 0) {
        journeyData?.data.current_aggregators.map((agg: any) => {
          if (agg.status) {
            append({
              aggregator_name: agg.name,
              aggregator_id: agg.id,
              aggregator_fee: agg.sale?.aggregator_fee_percentage ?? undefined,
              payments: agg.related_payment_methods.map(
                (item: any) => `${item.payment_method.id}`
              ),
              payments_Names: agg.related_payment_methods.map(
                (item: any) => item.payment_method.name
              ),
              delivery_type:
                agg.sale?.delivery_type === "by_order" ? "order" : "amount",
              delivery_cost:
                agg.sale?.delivery_type === "by_order"
                  ? agg.sale?.delivery_fee_per_order
                  : agg.sale?.delivery_fee,
              net_sales_with_tax: "",
              order_quantity: "",
              is_keta: agg.is_keta,
            });
          }
        });
      }
    }
  }, [journeyData, isFetching, getJourneyStatus, watch("journey_type")]);

  useEffect(() => {
    console.log("journeyData changes");
    if (mode === "add") {
      // Check new payments in add sale for foodics files
      if (journeyData?.data?.new_payment_methods?.length > 0 && !isOnboarding) {
        setNotifications([
          ...notifications,
          {
            type: "warning",
            message: t("foundPayments"),
          },
        ]);
        appendNewPaymentsAgg({
          aggregator_name: "",
          aggregator_fee: "",
          payments: [],
        });
      } else if (currentStep === 1) {
        setCurrentStep((prev) => prev + 1);
        console.log("happenss");
      }
      // Set netSales from journey when manual and branchHasMenuFeature
      if (
        getValues("journey_type") === "manual" &&
        getValues("branchHasMenuFeature")
      ) {
        setValue("net_sales_with_tax", journeyData?.data?.sale?.gross_sales);
      } else {
        setValue("net_sales_with_tax", "");
      }
      // Set discount from journey when manual or excel and branchHasMenuFeature
      if (
        (getValues("journey_type") === "manual" ||
          getValues("journey_type") === "excel") &&
        getValues("branchHasMenuFeature")
      ) {
        setValue("discount", journeyData?.data?.sale?.discount ?? 0);
      }
    }
    // Reset the flow with journey data to edit it
    if (mode == "edit" && journeyData) {
      reset({
        mode: "edit",
        journey_type: journeyData.data.type,
        dateRange: {
          start_date: journeyData.data.start_date,
          end_date: journeyData.data.end_date,
        },
        branch_id: journeyData.data.branch,
        COGS_manual_type:
          journeyData.data.cogs.is_percentage &&
          journeyData.data.cogs.type !== "formula"
            ? "percentage"
            : "amount",
        COGS_value:
          journeyData.data.cogs.is_percentage &&
          journeyData.data.cogs.type !== "formula"
            ? journeyData.data.cogs.percentage
            : journeyData.data.cogs.amount,
        net_sales_with_tax:
          journeyData.data.type === "manual"
            ? journeyData.data.sale.gross_sales
            : "",
        total_order_quantity:
          journeyData.data.type === "manual" ||
          journeyData.data.type === "excel"
            ? journeyData.data.sale.quantity
            : "",
        discount: journeyData.data.sale.discount ?? "",
        // other_revenue_include_tax: journeyData.data.sale
        //   .exclude_other_revenue_tax
        //   ? "1"
        //   : "0",
        hasOtherRevenue:
          journeyData.data.sale?.other_revenue > 0 ? "yes" : "no",
        other_revenue: journeyData.data.sale?.include_other_revenue_tax
          ? journeyData.data.sale?.other_revenue +
            journeyData.data.sale?.other_revenue_tax
          : journeyData.data.sale?.other_revenue,
        other_revenue_tax: journeyData.data.sale?.other_revenue_tax,
        include_other_revenue_tax: journeyData.data.sale
          ?.include_other_revenue_tax
          ? "1"
          : "0",
        otherRevenuePayments:
          journeyData.data.otherRevenuePayments.map((pay: any) => {
            return {
              payment_method_id: pay.paymentMethod.id,
              amount: pay.total,
            };
          }) || [],
        tips: journeyData.data.sale.tips ?? "",
        has_jazr_calculations:
          journeyData.data.sale.tax_type === "jazr" ? "yes" : "no",
        ...(journeyData.data.type === "manual"
          ? {
              paymentDiscount: journeyData.data.sale?.journey_discount ?? 0,
              hasPaymentDiscount:
                journeyData.data.sale?.journey_discount > 0 ? "yes" : "no",
              discountPayments:
                journeyData.data.discount_payments.map((pay: any) => {
                  return {
                    payment_method_id: pay.paymentMethod.id,
                    amount: pay.total,
                    name: pay.paymentMethod.name,
                  };
                }) || [],
            }
          : {}),
      });

      setCOGSType(journeyData.data.cogs.type);

      // set aggregators to edit it
      journeyData?.data.aggregator_sales.forEach((agg: any) => {
        append({
          aggregator_name: agg.aggregator.name,
          aggregator_id: agg.aggregator.id,
          aggregator_sale_id: agg.id,
          aggregator_fee: agg.aggregator_fee_percentage,
          payments: agg.related_payment_methods.map(
            (item: any) => `${item.payment_method?.id}`
          ),
          payments_Names: agg.related_payment_methods.map(
            (item: any) => item.payment_method?.name
          ),
          delivery_type: agg.delivery_type === "by_order" ? "order" : "amount",
          delivery_cost:
            agg.delivery_type === "by_order"
              ? agg.delivery_fee_per_order
              : agg.delivery_fee,
          net_sales_with_tax: agg.sales,
          order_quantity: agg.quantity,
          is_keta: agg.aggregator.is_keta,
        });
      });

      // set payments to edit it
      if (mode === "edit") {
        journeyData.data.payments.forEach((payment: any) => {
          balanceAppend({
            amount: payment.total as string,
            quantity: payment.quantity as string,
            // agg_id: field.id,
            id: payment.id as string,
            name: payment.paymentMethod.name,
            payment_method_id: payment.paymentMethod.id,
            is_discount: payment.paymentMethod.is_discount,
          });
        });
      }
    }
  }, [journeyData]);

  useEffect(() => {
    // Remove new payments aggs fields if files change
    if (filesChanged) {
      replaceNewPaymentsAgg([]);
    }
  }, [filesChanged]);

  useEffect(() => {
    // Manual onboarding balance
    // let manualTotalAmount = 0;
    // let manualTotalQuantity = 0;
    if (
      journeyType === "manual" &&
      isOnboarding &&
      currentStep === steps.length - 2
    ) {
      replaceBalance([]);

      if (
        balanceFields[balanceFields.length - 1]?.agg_id === "branch_balance"
      ) {
        balanceRemove([balanceFields.length - 1]);
      }

      if (fields.length > 0) {
        fields.forEach((field: any, index: number) => {
          // manualTotalAmount += Number(
          //   watch("aggregators")[index].net_sales_with_tax
          // );
          // manualTotalQuantity += Number(
          //   watch("aggregators")[index].order_quantity
          // );
          balanceAppend({
            amount: watch("aggregators")[index].net_sales_with_tax as string,
            quantity: watch("aggregators")[index].order_quantity as string,
            agg_id: field.id,
            id: field.id,
            name: watch("aggregators")[index].aggregator_name,
          });
        });
      }
      // console.log(
      //   { manualTotalAmount, manualTotalQuantity },
      //   getValues("net_sales_with_tax")
      // );
      // if (+getValues("net_sales_with_tax") - manualTotalAmount > 0) {
      //   balanceAppend({
      //     amount: `${+getValues("net_sales_with_tax") - manualTotalAmount}`,
      //     quantity: `${
      //       Number(getValues("total_order_quantity")) - manualTotalQuantity
      //     }`,
      //     agg_id: `branch_balance`,
      //     name:
      //       // getValues("branch_name") ||
      //       allBranches.find(
      //         (branch) => branch.id === Number(selectedBranch?.id)
      //       )?.name,
      //     is_branch: true,
      //   });
      // }
    }
  }, [fields, currentStep]);

  // Set balance with all payments in manual add sale and distribution aggs
  useEffect(() => {
    console.log("distribution manual aggs balance in add", {
      journeyType,
      mode,
      paymentsData,
      isOnboarding,
    });
    if (
      paymentsData &&
      !isOnboarding &&
      journeyType === "manual" &&
      mode === "add" &&
      currentStep === steps.length - 2 &&
      balanceFields.length === 0
    ) {
      // replaceBalance([]);
      paymentsData.data
        .filter(
          (pay) =>
            pay.status && pay.type !== "other_revenue" && !pay.is_discount
        )
        .forEach((payment, index) => {
          let isPaymentFromAgg = false;
          let aggIndex = 0;
          getValues("aggregators").forEach((agg, index) => {
            if (`${agg.payments?.[0]}` === `${payment.id}`) {
              isPaymentFromAgg = true;
              aggIndex = index;
            }
          });
          console.log({ isPaymentFromAgg });
          balanceAppend({
            amount: isPaymentFromAgg
              ? (getValues("aggregators")[aggIndex]
                  .net_sales_with_tax as string)
              : "0",
            quantity: isPaymentFromAgg
              ? (getValues("aggregators")[aggIndex].order_quantity as string)
              : "0",
            agg_id: ``,
            name: payment.name,
            payment_method_id: `${payment.id}`,
            is_discount: payment.is_discount,
          });
        });
    }
  }, [
    paymentsData,
    isOnboarding,
    journeyType,
    mode,
    watch("journey_type"),
    watch("net_sales_with_tax"),
    watch("total_order_quantity"),
    currentStep,
    // watch("aggregators"),
    // fields,
    // getValues("aggregators")
  ]);

  // Add new payments aggs to aggs steps
  useEffect(() => {
    console.log("new payments aggs change");
    if (currentStep === 2 && journeyType === "foodics") {
      const foodicsFilesAggsLength =
        journeyData?.data.current_aggregators.length;
      if (fields.length > foodicsFilesAggsLength) {
        // means that they already added to aggs
        const fieldsCopy = [...fields];
        // Remove the last x added items
        const newArr = fieldsCopy.slice(
          0,
          -(fields.length - foodicsFilesAggsLength)
        );
        replaceAggregators(newArr);
      }
      // add new payments aggs if yes
      if (watch("are_payments_new_agg") === "yes") {
        if (newPaymentsAggFields.length > 0) {
          watch("new_payments_aggregators").forEach((agg) => {
            append({
              aggregator_name: agg.aggregator_name as string,
              aggregator_id: "",
              aggregator_fee: agg.aggregator_fee as string,
              payments: agg.payments,
              delivery_type: "order",
              delivery_cost: "0",
              net_sales_with_tax: "",
              order_quantity: "",
              payments_Names: journeyData?.data?.new_payment_methods
                .filter((payment: any) =>
                  agg.payments?.includes(`${payment.id}`)
                )
                .map((item: any) => `${item.name}`),
            });
          });
        }
      }
    }
  }, [currentStep]);

  console.log(
    { fields },
    watch("aggregators"),
    watch("new_payments_aggregators")
  );

  // Update company success and error handling
  useEffect(() => {
    if (updateCompanyStatus === "success") {
      const newAuth = {
        ...auth,
        permissions: updateCompanyResponse.data.roles,
        //to set company name after adding it
        user: {
          ...auth.user,
          company: { ...auth.user?.company, name: getValues("company_name") },
        },
      };
      setAuth(newAuth);
      window.location.reload();
      // setBranchId(updateCompanyResponse?.data?.branchId);
      // if (!addedPackageToBranch) {
      //   handleOpen();
      //   return;
      // }
      // setCurrentStep((prev) => prev + 1);
    } else if (updateCompanyStatus === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            updateCompanyError?.data.errors[0].value ||
            generalT("somethingWrong"),
        },
      ]);
    }
  }, [updateCompanyStatus]);

  // Create journey success and error handling
  useEffect(() => {
    if (createJourneyStatus === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("dataIsAdded"),
        },
      ]);
      navigate("/dashboard");
    } else if (createJourneyStatus === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            createJourneyError?.data?.errors?.[0]?.value ||
            generalT("somethingWrong"),
        },
      ]);
    }
  }, [createJourneyStatus]);

  // Update journey success and error handling
  useEffect(() => {
    if (updateJourneyStatus === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("updatedSuccessfully"),
        },
      ]);
      navigate("/sales/list");
    } else if (updateJourneyStatus === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            updateJourneyError?.data?.errors?.[0]?.value ||
            generalT("somethingWrong"),
        },
      ]);
    }
  }, [updateJourneyStatus]);

  useEffect(() => {
    if (logoutStatus === "success") {
      localStorage.clear();
      setAuth({ user: null, token: "", permissions: null });
      window.location.reload();
    } else if (logoutStatus === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: logoutError?.data.errors[0].value || t("somethingWrong"),
        },
      ]);
    }
  }, [logoutStatus]);

  // For switching between journey types (foodics and manual)
  useEffect(() => {
    if (searchParams.get("journeyId")) {
      mutateDeletePaymentMethods(searchParams.get("journeyId") as string);
    }
    setFilesChanged(true); // To reprocessing the data
    if (mode === "add") {
      // journey change only in add mode
      replaceBalance([]);
    }
  }, [journeyType]);

  //import journey by email
  useEffect(() => {
    setValue("branch_id", autoImportBranchId as string);
  }, [autoImportBranchId]);

  console.log(
    {
      errors,
      isOnboarding,
      journeyData,
      selectedBranchVat,
      journeyTaxPercentage,
      selectedBranch,
    },
    getValues()
  );

  // update aggs sales and quantity based on balance change
  const handleUpdateAggsWhenBalanceChange = () => {
    const currentAggs = [...getValues("aggregators")];
    const currentPayments = getValues("balance");
    const updatedAggregators = currentAggs.map((agg) => {
      let aggAmount = 0;
      let aggQuantity = 0;
      agg.payments?.forEach((id) => {
        const selectedPayment = currentPayments.find(
          (pay) => Number(pay.payment_method_id) === +id
        );
        aggAmount += Number(selectedPayment?.amount) ?? 0;
        aggQuantity += Number(selectedPayment?.quantity) ?? 0;
      });
      return {
        ...agg,
        net_sales_with_tax: aggAmount > 0 ? aggAmount : agg.net_sales_with_tax,
        order_quantity: aggQuantity > 0 ? aggQuantity : agg.order_quantity,
      };
    });
    replaceAggregators(updatedAggregators);
  };

  const balanceWatch = watch("balance");

  useEffect(() => {
    if (journeyType === "manual") {
      console.log("balance changed");
      handleUpdateAggsWhenBalanceChange();
    }
  }, [JSON.stringify(balanceWatch)]);

  useEffect(() => {
    if (currentStep === steps.length - 1) {
      handleUpdateAggsWhenBalanceChange();
    }
  }, [currentStep]);

  return (
    <Box>
      <Box px={{ xs: 3, md: 4 }} pt={4} pb={{ xs: 1, md: 0 }}>
        <img src={logo} alt="" />
        <Stack
          direction={"row"}
          gap={1.5}
          sx={{ position: "absolute", top: "30px", right: "30px" }}
        >
          <IconButton onClick={handleClickE2} size="small">
            <LanguageIcon sx={{ color: "#1D2939" }} />
          </IconButton>
          <Menu
            id="basic-menu"
            anchorEl={anchorE2}
            open={openE2}
            onClose={handleCloseE2}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            <MenuItem
              onClick={() => handleLanguageChange("en")}
              sx={{ gap: 1 }}
            >
              <img src={englandIcon} alt="" />
              <Typography
                color={"#1D2939"}
                fontSize={"16px"}
                textTransform={"capitalize"}
              >
                English
              </Typography>
            </MenuItem>
            <MenuItem
              onClick={() => handleLanguageChange("ar")}
              sx={{ gap: 1 }}
            >
              <img src={saudiIcon} alt="" />
              <Typography
                color={"#1D2939"}
                fontSize={"16px"}
                textTransform={"capitalize"}
              >
                العربية
              </Typography>
            </MenuItem>
          </Menu>
          {hasBranchesList ? (
            <IconButton
              onClick={() => {
                if (isOnboarding) {
                  navigate("/dashboard");
                } else {
                  navigate("/sales/list");
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          ) : (
            <Button onClick={handleLogout} variant="outlined" size="small">
              {t("logout")}
            </Button>
          )}
        </Stack>
      </Box>
      <Box display={"flex"} justifyContent={"center"} pb={"150px"}>
        {isLoadingBranches || isLoading || isLoadingJourneyData ? (
          <Box display={"flex"} alignItems={"center"} height={"60vh"}>
            <Lottie options={defaultLottieOptions} height={400} width={400} />
          </Box>
        ) : (
          <Box maxWidth={"736px"} width={"100%"} px={{ xs: 3, md: 0 }}>
            {currentStep === 0 && (
              <CompanyDetails
                control={control}
                errors={errors}
                branchesHavePermission={branchesHavePermission || []}
                hasBranchesList={hasBranchesList}
                mode={mode}
                watch={watch}
                isAutoImportedJourney={isAutoImportedJourney}
              />
            )}
            <Box sx={{ display: currentStep === 1 ? "block" : "none" }}>
              <CalculateSales
                mode={mode}
                control={control}
                errors={errors}
                watch={watch}
                setValue={setValue}
                newPaymentsAggFields={newPaymentsAggFields}
                appendNewPaymentsAgg={appendNewPaymentsAgg}
                removeNewPaymentsAgg={removeNewPaymentsAgg}
                newPayments={journeyData?.data?.new_payment_methods}
                journeyTaxPercentage={journeyTaxPercentage}
                journeyTaxDivider={journeyTaxDivider}
                journeysDateRange={journeysDateRange}
                setFilesChanged={setFilesChanged}
                filesChanged={filesChanged}
                otherRevenueFields={otherRevenueFields}
                appendOtherRevenue={appendOtherRevenue}
                removeOtherRevenue={removeOtherRevenue}
                discountPaymentsFields={discountPaymentsFields}
                appendDiscountPayment={appendDiscountPayment}
                removeDiscountPayment={removeDiscountPayment}
                paymentsList={paymentsData?.data || []}
                isAutoImportedJourney={isAutoImportedJourney}
                foodicsType={foodicsType}
                setFoodicsType={setFoodicsType}
              />
            </Box>
            {currentStep === 2 && (
              <CalculateCOGS
                mode={mode}
                control={control}
                errors={errors}
                watch={watch}
                COGSType={COGSType}
                setCOGSType={setCOGSType}
                journeyData={journeyData?.data}
                journeyTaxDivider={journeyTaxDivider}
                lastCogs={lastCogsData?.data}
              />
            )}
            {currentStep === 3 && isOnboarding && (
              <AddAggregators
                control={control}
                errors={errors}
                watch={watch}
                append={append}
                replaceAggregators={replaceAggregators}
              />
            )}
            {((isOnboarding &&
              steps.length > 6 &&
              currentStep >= 4 &&
              currentStep < steps.length - 2) ||
              (!isOnboarding &&
                steps.length > 5 &&
                currentStep >= 3 &&
                currentStep < steps.length - 2)) && (
              <AggregatorDetails
                control={control}
                errors={errors}
                watch={watch}
                index={currentStep - aggregatorsStartStep}
                journeyData={journeyData?.data}
                isOnboarding={isOnboarding}
                journeyTaxPercentage={journeyTaxPercentage}
                mode={mode}
                aggsList={aggregatorsData?.data}
                setValue={setValue}
              />
            )}
            {currentStep === steps.length - 2 && (
              <MakeBalance
                balanceFields={balanceFields}
                control={control}
                errors={errors}
                getValues={getValues}
                journeyData={journeyData?.data}
                watch={watch}
                balanceAmountMatch={balanceAmountMatch}
                setBalanceAmountMatch={setBalanceAmountMatch}
                balanceQntyMatch={balanceQntyMatch}
                setBalanceQntyMatch={setBalanceQntyMatch}
                isOnboarding={isOnboarding}
                mode={mode}
              />
            )}
            {currentStep === steps.length - 1 && (
              <Summary
                journeyData={journeyData?.data}
                watch={watch}
                COGSType={COGSType}
                journeyTaxPercentage={journeyTaxPercentage}
              />
            )}
          </Box>
        )}
      </Box>
      {/* progress bar */}
      <Box
        bgcolor={"white"}
        zIndex={2}
        width={"100%"}
        position={"fixed"}
        sx={{ bottom: "0" }}
        boxShadow={"0 -8px 50px -5px rgba(50, 50, 50, 0.1)"}
      >
        <BorderLinearProgress
          variant="determinate"
          value={
            isLoadingJourneyData ? 0 : ((currentStep + 1) / steps.length) * 100
          }
        />
        <Stack
          direction={"row"}
          justifyContent={"space-around"}
          m={3}
          gap={"10px"}
        >
          {currentStep > 0 ? (
            <Button onClick={goBack} variant="outlined" color="tertiary">
              {generalT("back")}
            </Button>
          ) : (
            <Box />
          )}

          <Stack direction={"row"} gap={"10px"} alignItems={"center"}>
            {isOnboarding &&
              currentStep > 4 &&
              steps.length > 7 &&
              currentStep < steps.length - 2 && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={deleteAggregator}
                >
                  {generalT("delete")} {generalT("aggregator")}
                </Button>
              )}
            {isOnboarding &&
              currentStep > 3 &&
              currentStep === steps.length - 3 && (
                <Button
                  variant="outlined"
                  color="tertiary"
                  onClick={addAnotherAggregator}
                >
                  {t("addAnotherAgg")}
                </Button>
              )}
            {isOnboarding && currentStep === 1 && (
              <Link
                color={"#1D2939"}
                component={RouterLink}
                underline="always"
                to="/dashboard"
                fontWeight={500}
                sx={{ mx: 1 }}
              >
                {t("skip")}
              </Link>
            )}
            {journeyType === "foodics" &&
            foodicsType === "connectFoodics" &&
            currentStep === 1 ? null : (
              <LoadingButton
                variant="contained"
                endIcon={
                  <ArrowForwardIosIcon
                    sx={{
                      height: "15px",
                      transform: i18n.language === "ar" ? "scaleX(-1)" : "",
                    }}
                  />
                }
                sx={{ display: "flex" }}
                onClick={goNext}
                loading={
                  updateCompanyIsPending ||
                  isPendingCreateOrders ||
                  loading ||
                  isPendingCreateJourney ||
                  isPendingUpdateJourney ||
                  isLoadingJourneyData ||
                  isFetchingDraftJourney ||
                  isPendingProcessProductSales ||
                  isPendingProcessExcelSales
                }
                disabled={branchRequiresPackageRenew}
              >
                {currentStep < steps.length - 1 ? t("next") : t("finish")}
              </LoadingButton>
            )}
          </Stack>
        </Stack>
      </Box>

      {open && (
        <BranchPackagePopup
          open={open}
          handleClose={handleClose}
          branchId={`${selectedBranch?.id}`}
          setAddedPackageToBranch={setAddedPackageToBranch}
          refetchBranches={refetchBranches}
        />
      )}
    </Box>
  );
};

export default AddAEditSalesPage;
